import { ApolloProvider, useReactiveVar } from '@apollo/client';
import { Authenticator, Permission } from '../Authenticator/Authenticator';
import { PropsWithChildren, lazy, useEffect } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import { appStateSchema, isDeepLinkVar, stateVars } from '../../state/appState';

import { AppStateProvider } from '../AppStateProvider/AppStateProvider';
import { DeepLinkProvider } from '../DeepLinkProvider/DeepLinkProvider';
import { QueryClientProvider } from '../../QueryClientProvider';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RequireCause } from '../RequireCause/RequireCause';
import ScrollToTop from '../ScrollToTop/ScrollToTop';
import { client } from '../../apolloClient';
import { routes } from '../../constants';
import styled from 'styled-components';
import { useAuthenticatedUser } from '../../hooks/useAuthenticatedUser';
import { useLoginPageUrl } from '../../hooks/useLoginPageUrl';
import { useOptimizely } from '../../Optimizely/CustomOptimizelyProvider';

const ChooseFundraisingTypePage = lazy(() =>
  import(
    /* webpackChunkName: "ChooseFundraisingTypePage" */ '../../pages/fundraising/ChooseFundraisingTypePage/ChooseFundraisingTypePage'
  )
);

const ChooseCause = lazy(() =>
  import(
    /* webpackChunkName: "ChooseCause" */ '../../pages/fundraising/ChooseCause/ChooseCause'
  )
);

const ChooseEvent = lazy(() =>
  import(
    /* webpackChunkName: "ChooseEvent" */ '../../pages/fundraising/ChooseEvent/ChooseEvent'
  )
);

const Activity = lazy(() =>
  import(
    /* webpackChunkName: "Activity" */ '../../pages/fundraising/Activity/Activity'
  )
);

const Personalise = lazy(() =>
  /* webpackChunkName: "Personalise" */ import(
    '../../pages/fundraising/Personalise/Personalise'
  )
);

const Cover = lazy(() =>
  import(/* webpackChunkName: "Cover" */ '../../pages/fundraising/Cover/Cover')
);

const Target = lazy(() =>
  import(
    /* webpackChunkName: "Target" */ '../../pages/fundraising/Target/Target'
  )
);

const Occasion = lazy(() =>
  import(
    /* webpackChunkName: "Occasion" */ '../../pages/fundraising/Occasion/Occasion'
  )
);

const ActivitySelection = lazy(() =>
  import(
    /* webpackChunkName: "ActivitySelection" */ '../../pages/fundraising/ActivitySelection/ActivitySelection'
  )
);

const InMemory = lazy(() =>
  import(
    /* webpackChunkName: "ActivitySelection" */ '../../pages/fundraising/InMemory/InMemory'
  )
);

const InMemoryActivitySelection = lazy(() =>
  import(
    /* webpackChunkName: "InMemoryActivitySelection" */ '../../pages/fundraising/InMemoryActivitySelection/InMemoryActivitySelection'
  )
);

const Summary = lazy(() =>
  import(
    /* webpackChunkName: "Summary" */ '../../pages/fundraising/Summary/Summary'
  )
);

const TeamOverview = lazy(() =>
  /* webpackChunkName: "TeamOverview" */ import(
    '../../pages/fundraising/TeamOverview/TeamOverview'
  )
);

export default function FundraisingRouter() {
  return (
    <QueryClientProvider>
      <ApolloProvider client={client}>
        <DeepLinkProvider>
          <AppStateProvider
            stateVars={stateVars}
            appStateSchema={appStateSchema}
          >
            <Container>
              <Step>
                <ScrollToTop />
                <Switch>
                  <Route
                    path={routes.root}
                    exact
                    component={ChooseFundraisingTypePage}
                  />
                  <Route
                    path={routes.teamOverview}
                    exact
                    component={TeamOverview}
                  />
                  <Switch>
                    <Route
                      path={routes.cause}
                      exact
                      render={() => (
                        <FundraisingAuthenticator>
                          <ChooseCause />
                        </FundraisingAuthenticator>
                      )}
                    />
                    <RequireCause>
                      <Route
                        path={routes.personalise}
                        exact
                        render={() => (
                          <FundraisingAuthenticator>
                            <Personalise />
                          </FundraisingAuthenticator>
                        )}
                      />
                      <Route
                        path={routes.cover}
                        render={() => (
                          <FundraisingAuthenticator>
                            <Cover />
                          </FundraisingAuthenticator>
                        )}
                      />
                      <Route
                        path={routes.inMemory}
                        exact
                        render={() => (
                          <FundraisingAuthenticator>
                            <InMemory />
                          </FundraisingAuthenticator>
                        )}
                      />
                      <Route
                        path={routes.activitySelection}
                        exact
                        render={() => (
                          <FundraisingAuthenticator>
                            <ActivitySelection />
                          </FundraisingAuthenticator>
                        )}
                      />
                      <Route
                        path={routes.events}
                        exact
                        render={() => (
                          <FundraisingAuthenticator>
                            <ChooseEvent />
                          </FundraisingAuthenticator>
                        )}
                      />
                      <Route
                        path={routes.inMemoryActivitySelection}
                        exact
                        render={() => (
                          <FundraisingAuthenticator>
                            <InMemoryActivitySelection />
                          </FundraisingAuthenticator>
                        )}
                      />
                      <Route
                        path={routes.occasion}
                        exact
                        render={() => (
                          <FundraisingAuthenticator>
                            <Occasion />
                          </FundraisingAuthenticator>
                        )}
                      />
                      <Route
                        path={routes.activity}
                        exact
                        render={() => (
                          <FundraisingAuthenticator>
                            <Activity />
                          </FundraisingAuthenticator>
                        )}
                      />
                      <Route
                        path={routes.target}
                        exact
                        render={() => (
                          <FundraisingAuthenticator>
                            <Target />
                          </FundraisingAuthenticator>
                        )}
                      />
                      <Route
                        path={routes.summary}
                        exact
                        render={() => (
                          <FundraisingAuthenticator>
                            <Summary />
                          </FundraisingAuthenticator>
                        )}
                      />
                    </RequireCause>
                  </Switch>
                </Switch>
              </Step>
            </Container>
          </AppStateProvider>
        </DeepLinkProvider>
      </ApolloProvider>
      <ReactQueryDevtools />
    </QueryClientProvider>
  );
}

function FundraisingAuthenticator({ children }: PropsWithChildren) {
  const loginUrl = new URL(useLoginPageUrl());
  const { optimizely } = useOptimizely();
  const isDeepLink = useReactiveVar(isDeepLinkVar);
  const { isUserLoggedIn } = useAuthenticatedUser();
  const { search } = useLocation();

  useEffect(() => {
    if (search.includes('experiment=frp_login_after_summary_step')) {
      // call decide endpoint again to ensure user is enrolled in experiment after redirect
      optimizely?.decide('frp_login_after_summary_step');
    }
  }, [optimizely, search]);

  if (!isDeepLink && !isUserLoggedIn) {
    const decision = optimizely?.decide('frp_login_after_summary_step');
    const returnUrl = new URL(
      loginUrl.searchParams.get('returnUrl') ?? window.location.href
    );
    returnUrl.searchParams.set('experiment', 'frp_login_after_summary_step');
    loginUrl.searchParams.set('returnUrl', returnUrl.toString());

    if (
      ['summary_login_popup', 'summary_login_link'].includes(
        decision?.variationKey ?? ''
      )
    ) {
      return children;
    }
  }

  return (
    <Authenticator
      allowedPermissions={[
        Permission.Staff,
        Permission.Charity,
        Permission.Consumer,
      ]}
      loginUrl={loginUrl.toString()}
    >
      {children}
    </Authenticator>
  );
}

const Container = styled.div`
  width: 100%;
  max-width: 100%;
  padding: 0;
  vertical-align: top;
  grid-area: step;
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: auto 1fr;
  grid-template-areas: 'progress' 'step';
  background-color: ${({ theme }) => theme.color.white};
`;

const Step = styled.div`
  grid-area: step;
`;
